// v2/hooks/useDebt.js — /api/v2/debt/ (FIFO borc yaslandirma) + /transactions/
import { useCallback, useEffect, useRef, useState } from 'react';
import { apiGet, qs } from '../utils/api';
import { useDebounced } from './useCatalog';

const sayi = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const nsayi = (v) => (v === null || v === undefined || v === '' ? null : sayi(v));
const dizi = (v) => (Array.isArray(v) ? v : []);

function mapDoc(x) {
  return {
    id: x.id, date: x.date || null, type: x.type || '', kind: x.kind || 'other',
    docNo: x.docNo || '', description: x.description || '',
    amount: sayi(x.amount), remaining: sayi(x.remaining),
    days: nsayi(x.days), bucket: x.bucket || null,
  };
}

function mapMove(x) {
  return {
    id: x.id, date: x.date || null, type: x.type || '', kind: x.kind || 'other',
    docNo: x.docNo || '', description: x.description || '',
    debit: sayi(x.debit), credit: sayi(x.credit), balance: sayi(x.balance),
  };
}

function mapSummary(v) {
  const d = v || {};
  const t = d.totals || {};
  const od = d.openDocs || {};
  const r = d.restriction;
  return {
    asOf: d.asOf || null,
    totals: {
      debit: sayi(t.debit), credit: sayi(t.credit), net: sayi(t.net), count: sayi(t.count),
      firstDate: t.firstDate || null, lastDate: t.lastDate || null,
    },
    buckets: dizi(d.buckets).map((b) => ({
      id: b.id, label: b.label, tone: b.tone, amount: sayi(b.amount), pct: sayi(b.pct),
    })),
    current: sayi(d.current),
    overdue: sayi(d.overdue),
    future: sayi(d.future),
    openTotal: sayi(d.openTotal),
    risk: d.risk || 'clean',
    oldestDays: nsayi(d.oldestDays),
    openDocs: { items: dizi(od.items).map(mapDoc), total: sayi(od.total), limited: Boolean(od.limited) },
    trend: dizi(d.trend).map((x) => ({
      id: x.id, label: x.label, year: sayi(x.year),
      debit: sayi(x.debit), credit: sayi(x.credit), balance: sayi(x.balance),
    })),
    restriction: r ? {
      active: Boolean(r.active), blocked: Boolean(r.blocked), daysLeft: nsayi(r.daysLeft),
      dueDate: r.dueDate || null, message: r.message || '',
      requiredPayment: sayi(r.requiredPayment), overdueDays: nsayi(r.overdueDays),
    } : null,
    paymentUrl: d.paymentUrl || '',
  };
}

export function useDebtSummary() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const yenile = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const v = await apiGet('/v2/debt/');
      setData(mapSummary(v));
    } catch (e) {
      setError(e.message || 'Borç bilgileri alınamadı');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { yenile(); }, [yenile]);

  return { data, loading, error, yenile };
}

const BOS = { items: [], total: 0, totalPages: 1, page: 1, stats: { debit: 0, credit: 0, net: 0 } };

export function useDebtTransactions(f, enabled = true) {
  const q = useDebounced(f.q, 350);
  const anahtar = qs({
    q: q || undefined,
    from: f.from || undefined,
    to: f.to || undefined,
    type: f.type || undefined,
    page: f.page,
    per_page: f.per,
  });

  const [sonuc, setSonuc] = useState(BOS);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const ilk = useRef(true);
  const abortRef = useRef(null);

  const getir = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    if (ilk.current) setLoading(true);
    else setRefreshing(true);
    setError(null);
    try {
      const v = await apiGet(`/v2/debt/transactions/${anahtar}`, { signal: ctrl.signal });
      if (ctrl.signal.aborted) return;
      const d = v || {};
      const s = d.stats || {};
      setSonuc({
        items: dizi(d.results).map(mapMove),
        total: sayi(d.total_items),
        totalPages: Math.max(sayi(d.total_pages), 1),
        page: Math.max(sayi(d.page), 1),
        stats: { debit: sayi(s.debit), credit: sayi(s.credit), net: sayi(s.net) },
      });
    } catch (e) {
      if (e && e.name === 'AbortError') return;
      setError(e.message || 'Hesap hareketleri alınamadı');
    } finally {
      if (ctrl.signal.aborted === false) {
        ilk.current = false;
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [anahtar]);

  useEffect(() => {
    if (enabled === false) return undefined;
    getir();
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [getir, enabled]);

  return { ...sonuc, loading, refreshing, error, yenile: getir };
}
